import { Box, Grid, Panel, Row } from '@indoqa/style-system';
import * as React from 'react';
import { FelaComponent, useFela } from 'react-fela';
import { Route, useLocation } from 'react-router';
import { ColorsPanel } from './colors/ColorsPanel';
import ContentHeader from './layout/ContentHeader';
import ContentHeading from './layout/ContentHeading';
import ContentPanel from './layout/ContentPanel';
import Logo from './layout/Logo';
import StyleGuideMenu from './layout/Menu';
import MenuGroup from './layout/MenuGroup';
import MenuHeader from './layout/MenuHeader';
import MenuIcon from './layout/MenuIcon';
import MenuItem from './layout/MenuItem';
import { OverviewPanel } from './overview/OverviewPanel';
import { TypographyPanel } from './typography/TypographyPanel';
import { UIEThemeContext } from './uietheme/UIEThemeContext';
import { uieLightTheme } from './uietheme/uieThemes';
import importCss from './utils/importCss';
import { cleanUrlPathPart } from './utils/urlUtils';
const InnerContentPanel = ({ name, uieTheme, children }) => {
    const style = {
        paddingTop: uieTheme.spacing.space4,
        paddingRight: uieTheme.spacing.space4,
        paddingBottom: uieTheme.spacing.space4,
        paddingLeft: uieTheme.spacing.space4,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ContentHeader, null, name && React.createElement(ContentHeading, null, name)),
        React.createElement(Box, { style: style }, children)));
};
const InnerStyleGuideMenu = ({ show, uieTheme, children }) => {
    const style = {
        paddingBottom: uieTheme.spacing.space2,
        display: show ? 'block' : 'none',
        tablet: {
            display: 'block',
            paddingTop: uieTheme.spacing.space4,
            paddingBottom: uieTheme.spacing.space4,
        },
    };
    return React.createElement(FelaComponent, { style: style }, children);
};
const MenuIconWrapper = ({ toggleMenu, uieTheme }) => {
    const style = {
        cursor: 'pointer',
        tablet: {
            display: 'none',
        },
    };
    const renderMenuIcon = ({ className }) => (React.createElement("div", { className: className, onClick: () => toggleMenu() },
        React.createElement(MenuIcon, { color: uieTheme.colors.menuIcon, size: "35" })));
    return React.createElement(FelaComponent, { style: style }, renderMenuIcon);
};
const createComponentRoute = (name, component, mountPath, uieTheme) => {
    const componentMountPath = `${mountPath}/${cleanUrlPathPart(name)}`;
    return (React.createElement(Route, { key: componentMountPath, exact: true, path: componentMountPath, render: () => (React.createElement(InnerContentPanel, { name: name, uieTheme: uieTheme }, component)) }));
};
const createMenuGroups = (groups, mountPath) => {
    return groups.map((componentDescription) => {
        const { name, descriptions } = componentDescription;
        const menuItems = descriptions.map((description) => {
            const componentMountPath = `${mountPath}/${cleanUrlPathPart(name)}/${cleanUrlPathPart(description.name)}`;
            return (React.createElement(MenuItem, { key: componentMountPath, to: componentMountPath }, description.name));
        });
        return (React.createElement(MenuGroup, { name: name, key: name }, menuItems));
    });
};
const createGroupsRoutes = (groups, mountPath, uieTheme) => {
    const routes = [];
    groups.forEach((componentDescription) => {
        const { name, descriptions } = componentDescription;
        descriptions.forEach((description) => {
            routes.push(createComponentRoute(description.name, description.component, `${mountPath}/${cleanUrlPathPart(name)}`, uieTheme));
        });
    });
    return routes;
};
export const UIExplorer = ({ colors, description, fontMixes, fontSizes, headlineFonts, groups, logo, mountPath, overviewPanel, projectName, showFundamentals = true, textFonts, textFontSize, uieTheme, }) => {
    const [localUieTheme] = React.useState(uieTheme || uieLightTheme);
    const [showMenu, setShowMenu] = React.useState(false);
    const location = useLocation();
    const { renderer } = useFela();
    React.useLayoutEffect(() => {
        importCss('style-guide-fonts', localUieTheme.fontFamilyCSSImports);
        renderer.renderStatic({
            backgroundColor: '#F0F2F5',
            paddingTop: '4px',
            paddingRight: '2px',
            paddingLeft: '2px',
        }, 'p code');
    }, [localUieTheme.fontFamilyCSSImports, renderer]);
    React.useLayoutEffect(() => {
        document.title = `${projectName} | UI-Explorer`;
    }, [projectName]);
    React.useLayoutEffect(() => {
        setShowMenu(false);
    }, [location]);
    const toggleMenu = () => {
        setShowMenu((prevState) => !prevState);
    };
    const getDescription = () => {
        if (description || description === '') {
            return description;
        }
        return `UI-Explorer ${projectName}`;
    };
    return (React.createElement(UIEThemeContext.Provider, { value: localUieTheme },
        React.createElement(Grid, { spacing: 0 },
            React.createElement(Row, { height: "100vh" },
                React.createElement(Panel, { width: "17.5rem" },
                    React.createElement(StyleGuideMenu, null,
                        React.createElement(MenuHeader, null,
                            React.createElement(Logo, { to: mountPath }, logo || projectName),
                            React.createElement(MenuIconWrapper, { toggleMenu: toggleMenu, show: showMenu, uieTheme: localUieTheme })),
                        React.createElement(InnerStyleGuideMenu, { show: showMenu, uieTheme: localUieTheme },
                            showFundamentals && (React.createElement(MenuGroup, { name: "Fundamentals" },
                                React.createElement(MenuItem, { to: `${mountPath}/colors` }, "Colors"),
                                React.createElement(MenuItem, { to: `${mountPath}/typography` }, "Typography"))),
                            createMenuGroups(groups, mountPath)))),
                React.createElement(Panel, { style: { minHeight: '100vh' } },
                    React.createElement(ContentPanel, null,
                        React.createElement(Route, { exact: true, path: `${mountPath}/`, render: () => (React.createElement(InnerContentPanel, { name: getDescription(), uieTheme: localUieTheme }, overviewPanel || (React.createElement(OverviewPanel, { colors: colors, fontMixes: fontMixes, fontSizes: fontSizes, textFontSize: textFontSize })))) }),
                        React.createElement(Route, { exact: true, path: `${mountPath}/colors`, render: () => (React.createElement(InnerContentPanel, { name: "Color Scheme", uieTheme: localUieTheme },
                                React.createElement(ColorsPanel, { colors: colors }))) }),
                        React.createElement(Route, { exact: true, path: `${mountPath}/typography`, render: () => (React.createElement(InnerContentPanel, { name: "Typography", uieTheme: localUieTheme },
                                React.createElement(TypographyPanel, { textFonts: textFonts, headlineFonts: headlineFonts, fontMixes: fontMixes, fontSizes: fontSizes, textFontSize: textFontSize }))) }),
                        createGroupsRoutes(groups, mountPath, localUieTheme)))))));
};
//# sourceMappingURL=UIExplorer.js.map